import type { components } from '../../api/tipos'
import type { LineaLista } from './lista'

export type ResultadoResolucion = components['schemas']['ResultadoResolucion']
export type FichaOdoo = components['schemas']['FichaOdoo']
export type LineaPedido = components['schemas']['LineaPedido']
export type ClienteAPublicarEntrada = components['schemas']['ClienteAPublicarEntrada']

/**
 * El modelo que comparten los dos modos.
 *
 * El individual es un masivo de una fila: el mismo `POST /resolver` compone las
 * lineas, la misma regla decide si se puede publicar y el mismo `POST /publicar`
 * las crea. Tener dos caminos distintos habria significado dos sitios donde
 * olvidarse de comprobar que la ficha esta elegida, y esa comprobacion es la que
 * evita mandarle el pedido de un cliente a otro.
 *
 * Sobre la respuesta del backend se superponen tres decisiones del
 * administrador, que viven solo en la pantalla:
 *
 * - `fichas`: que ficha de Odoo es la buena cuando el NIF esta compartido.
 * - `precios`: el precio de una linea, cuando el del catalogo esta desfasado.
 * - `anadidas`: los productos que se le suman al pedido de ese cliente.
 *
 * Las tres se aplican en `lineasDe` y en `aEntradaPublicar`, que son los dos
 * unicos sitios desde los que se llega a Odoo. Una decision que no pase por
 * ahi se veria en la pantalla y no se publicaria.
 */

export interface FilaAgendamiento {
  /** Identificador estable de la fila dentro de la pantalla. */
  clave: string
  /** Numero de linea del texto pegado. Solo en el modo masivo. */
  numero?: number
  /** El texto original de esa linea, para poder ensenar lo que no se entendio. */
  texto?: string
  nif: string
  /** Lo que fallo al leer la linea, antes de preguntarle nada al backend. */
  errorLocal?: string
  resultado?: ResultadoResolucion
}

/** Ficha elegida a mano, por clave de fila. */
export type FichasElegidas = Record<string, number>

/** Precio editado a mano, indexado por `clave|indiceDeLinea`. */
export type PreciosEditados = Record<string, number>

/**
 * Productos anadidos a mano al pedido de una fila, por clave de fila.
 *
 * Van **detras** de las lineas que compuso el backend, y ese orden importa:
 * los precios editados se indexan por posicion, asi que anadir al final deja
 * intactas las ediciones que ya hubiera.
 */
export type LineasAnadidas = Record<string, LineaPedido[]>

export function clavePrecio(clave: string, indice: number): string {
  return `${clave}|${indice}`
}

/**
 * La ficha de Odoo de esta fila, o `null` si no hay ninguna decidida.
 *
 * Lo que elige el administrador manda sobre lo que trajo el backend: si mira
 * las cuatro dependencias del Ayuntamiento y senala una, es que sabe cual es.
 */
export function partnerElegido(fila: FilaAgendamiento, fichas: FichasElegidas): number | null {
  const elegida = fichas[fila.clave]
  if (elegida !== undefined) return elegida
  return fila.resultado?.ficha?.id ?? null
}

function fichaDe(fila: FilaAgendamiento, fichas: FichasElegidas): FichaOdoo | null {
  const id = partnerElegido(fila, fichas)
  if (id === null) return null
  if (fila.resultado?.ficha?.id === id) return fila.resultado.ficha
  return fila.resultado?.candidatos?.find((candidata) => candidata.id === id) ?? null
}

/** Cuantas lineas trajo el backend. Las de despues son anadidas a mano. */
export function lineasDelBackend(fila: FilaAgendamiento): number {
  return (fila.resultado?.pedido?.lineas ?? []).length
}

/**
 * Las lineas que se publicarian: las que compuso el backend mas las anadidas,
 * todas con los precios que haya tocado el administrador.
 */
export function lineasDe(
  fila: FilaAgendamiento,
  precios: PreciosEditados,
  anadidas: LineasAnadidas = {},
): LineaPedido[] {
  const lineas = [
    ...(fila.resultado?.pedido?.lineas ?? []),
    ...(anadidas[fila.clave] ?? []),
  ]
  return lineas.map((linea, indice) => {
    const editado = precios[clavePrecio(fila.clave, indice)]
    return editado === undefined ? linea : { ...linea, precio: editado }
  })
}

/**
 * El resumen que se lee en el evento del calendario y en la ruta.
 *
 * El del backend se respeta tal cual: dice "4 x BONO 4 18.9L", con las cuatro
 * garrafas, y eso no se puede recomponer desde las lineas (la del bono lleva
 * cantidad 1). Lo anadido se le concatena detras, en el mismo formato.
 *
 * Las cantidades negativas se quedan fuera, como en el backend: la devolucion
 * de envases es contabilidad, no algo que el repartidor tenga que cargar.
 */
export function resumenPedido(fila: FilaAgendamiento, anadidas: LineasAnadidas = {}): string {
  const base = fila.resultado?.pedido?.pedido_txt ?? ''
  const extra = (anadidas[fila.clave] ?? [])
    .filter((linea) => linea.cantidad > 0)
    .map((linea) => `${linea.cantidad} x ${linea.nombre}`)
  return [base, ...extra].filter((trozo) => trozo !== '').join(' + ')
}

/**
 * Por que esta fila no se puede publicar, o `null` si se puede.
 *
 * Devuelve el motivo y no un booleano a proposito: la tabla tiene que decir
 * **que** hay que arreglar. "No se puede publicar" sin mas obligaria al
 * administrador a adivinar cual de las veinte filas le falta algo.
 */
export function motivoNoPublicable(
  fila: FilaAgendamiento,
  fichas: FichasElegidas,
  anadidas: LineasAnadidas = {},
): string | null {
  if (fila.errorLocal) return fila.errorLocal

  const resultado = fila.resultado
  if (!resultado) return 'Esta linea todavia no se ha resuelto.'
  if (!resultado.cliente) {
    return `El NIF ${fila.nif} no esta en la lista de clientes: el agendamiento no da de alta clientes nuevos.`
  }
  if (partnerElegido(fila, fichas) === null) {
    return 'Falta elegir la ficha de Odoo de este cliente. Sin ella el pedido podria salir a otra direccion.'
  }
  // El error del backend deja de importar en cuanto hay lineas anadidas a
  // mano: "no se ha podido deducir su pedido habitual" es justo lo que el
  // administrador acaba de resolver eligiendo los productos.
  if (lineasDe(fila, {}, anadidas).length === 0) {
    return resultado.error ?? 'Esta fila no tiene ninguna linea de pedido.'
  }
  if (resultado.error && (anadidas[fila.clave] ?? []).length === 0) return resultado.error
  return null
}

export function aEntradaPublicar(
  fila: FilaAgendamiento,
  fichas: FichasElegidas,
  precios: PreciosEditados,
  anadidas: LineasAnadidas = {},
  fecha?: string,
): ClienteAPublicarEntrada | null {
  if (motivoNoPublicable(fila, fichas, anadidas) !== null) return null

  const cliente = fila.resultado?.cliente
  const ficha = fichaDe(fila, fichas)
  if (!cliente) return null

  return {
    contact_id: cliente.contact_id,
    nombre: cliente.nombre,
    code: cliente.code,
    partner_id: partnerElegido(fila, fichas),
    // La direccion es la de la **ficha de Odoo elegida**, no la de Postgres:
    // en un NIF compartido las dependencias tienen direcciones distintas, y el
    // evento del calendario tiene que llevar aquella a la que va el albaran.
    direccion: ficha?.calle || cliente.direccion || '',
    codigo_postal: ficha?.codigo_postal || cliente.codigo_postal || '',
    telefono: ficha?.telefono || cliente.telefono || '',
    lineas: lineasDe(fila, precios, anadidas),
    pedido_txt: resumenPedido(fila, anadidas),
    nota: '',
    // Fecha por cliente (B.3): solo si difiere de la global; si no, se omite.
    ...(fecha ? { fecha } : {}),
  }
}

/** Las filas que se publicarian ahora mismo. Las demas se quedan y se dice por que. */
export function publicables(
  filas: FilaAgendamiento[],
  fichas: FichasElegidas,
  anadidas: LineasAnadidas = {},
): FilaAgendamiento[] {
  return filas.filter((fila) => motivoNoPublicable(fila, fichas, anadidas) === null)
}

/**
 * Cose el analisis local con la respuesta del backend.
 *
 * `POST /resolver` solo recibe las lineas que se entendieron, y responde en ese
 * mismo orden. Aqui se vuelven a intercalar las que no se entendieron, en su
 * sitio: la revision tiene que ensenar la lista **entera**, o el administrador
 * publicaria diecinueve de veinte clientes sin enterarse del que falta.
 */
export function filasDeLista(
  lineas: LineaLista[],
  resultados: ResultadoResolucion[],
): FilaAgendamiento[] {
  let siguiente = 0
  return lineas.map((linea) => ({
    clave: `l${linea.numero}`,
    numero: linea.numero,
    texto: linea.texto,
    nif: linea.nif,
    errorLocal: linea.error,
    resultado: linea.error === undefined ? resultados[siguiente++] : undefined,
  }))
}
